import React, {useEffect, useState} from 'react';
import {useSelector} from "react-redux";

import useDataSource from "../../../../hooks/useDataSource";

import ReportTable from "./ReportTable";

import axios from "axios";
import {axiosDelay} from "../../../../config/config";

import { styled, LinearProgress } from '@mui/material';

const PREFIX = 'ReportList';

const classes = {
    root: `${PREFIX}-root`,
    formControl: `${PREFIX}-formControl`,
    selectEmpty: `${PREFIX}-selectEmpty`,
    linearProgress: `${PREFIX}-linearProgress`
};

const Root = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        width: '100%',
        height: '100%',
        overflow: 'auto'
    },

    [`& .${classes.formControl}`]: {
        margin: '8px',
        width: 'calc(100% - 16px)'
    },

    [`& .${classes.selectEmpty}`]: {
        marginTop: theme.spacing(2),
    },

    [`& .${classes.linearProgress}`]: {
        height: '1px'
    }
}));

export default function ReportList({id, configuration}) {

    const dataSource = useDataSource(configuration.dataSource.name);

    // Component State
    const [polishedReports, setPolishedReports] = useState(null);
    const [listStatus, setListStatus] = useState({fetching: false, fetched: false, data: null, error: null});
    const [gId, setGId] = useState(null);

    // App State
    const config = useSelector(state => state.config.data);
    const user = useSelector(state => state.user);
    const filters = useSelector(state => state.data.filters);
    const reportSelected = useSelector(state => state.report.reportSelected);
    const reportCreate = useSelector(state => state.report.reportCreate);

    const filter = filters.find(f => f.field === 'wbsgid');
    const condition = filter ? filter.conditions[0] : null;

    useEffect(() => {
        // If we're not filtering an we already have
        if (!configuration.element.filterByGId) {
            return;
        }

        // Clear the list and wait for the new report to be selected (new object) to fetch our list
        setListStatus({
            fetching: true,
            fetched: false,
            data: null,
            error: null
        });
        setPolishedReports(null);

    }, [condition]);

    useEffect(() => {

        // If we're not filtering an we already have
        if (!configuration.element.filterByGId && polishedReports && polishedReports.length > 0) {
            return;
        }

        // If we're filtering and we do not yet have wbsgid then bail
        if (!condition && configuration.element.filterByGId)
            return;

        // If there is a condition and we are filtering then pull out wbsgid
        let wbsgid = "";
        if (condition && configuration.element.filterByGId) {
            const conditions = filter.conditions[0];
            const parts = conditions.split(' ');
            wbsgid = parts[1];
        }

        setGId(wbsgid);

        fetchList(wbsgid, reportSelected.name ? reportSelected.name : "");

    }, [reportSelected])

    useEffect(() => {
        // Reload if a new report has been created
        if (reportCreate.fetched === true && !reportCreate.error) {
            fetchList(gId, reportSelected.name ? reportSelected.name : "");
        }
    }, [reportCreate.fetched])

    // Filename: gid_-report_reportLabel_unixtime -> 43 \DarkWaters\Test Test Report 01/08/2020
    // We do not have a nice name to put into a table so we'll look those up from the gantt datasource (driving wbsgid)
    useEffect(() => {

        // If we're not filtering an we already have
        if (!configuration.element.filterByGId && polishedReports && polishedReports.length > 0) {
            return;
        }

        if (listStatus.data) {
            const pReports = [];

            for (const uri of listStatus.data) {

                // Parse the uri and lookup friendly names
                // to build a user friendly table
                let uriParts = uri.split('/');
                uriParts = uriParts[uriParts.length - 1];

                // Filename
                uriParts = uriParts.split('.')[0];

                // GID, Report, Report Label, Timestamp
                uriParts = uriParts.split('_');

                const gid = parseInt(uriParts[0]);
                const report = uriParts[1].replaceAll('-', '/');
                const reportLabel = uriParts[2].replaceAll('SPC', ' ');
                const timeStamp = new Date(uriParts[3] * 1000);

                // Output for new object
                let displayName = null;

                // Lookup name
                const node = dataSource.data.find(d => d.gid === gid);
                if (node) {
                    displayName = node.displayname;
                }

                // If everything checks out then we'll display it to the user
                if (displayName) {
                    pReports.push({
                        displayName: displayName,
                        reportName: report,
                        reportLabel: reportLabel,
                        timeStamp: timeStamp,
                        uri: uri
                    })
                }
            }

            setPolishedReports(pReports);
        } else {
            setPolishedReports(null);
        }

    }, [listStatus]);

    const fetchList = (gid, report) => {

        const program = config.title.replaceAll(" ", "").toLowerCase();
        program.replace(/[^A-Z0-9]/ig, "");
        const username = user.email.split('@')[0];
        const database = config.authentication.apiSource;
        const loaderAPIUri = config.dataLoader.apiDataLoader;

        setListStatus({
            fetching: true,
            fetched: false,
            data: null,
            error: null
        });

        // Reports created for this user
        const getRequest = "{0}Report/List?program={1}&username={2}&report={3}&gId={4}&db={5}".format(loaderAPIUri, program, username, report, gid, database);

        setTimeout(function(){
            axios.get(getRequest)
                .then(function (response) {

                    setListStatus({
                            fetching: false,
                            fetched: true,
                            data: response.data,
                            error: null
                    });

                }).catch(function (error) {

                    setListStatus({
                        fetching: false,
                        fetched: true,
                        data: null,
                        error: error
                    });

                });
        }, JSON.parse(axiosDelay()))

    };

    return (
        <Root className={classes.root}>
            {!listStatus.fetched &&
                <LinearProgress className={classes.linearProgress}/>
            }
            {listStatus.fetched &&
                <div className={classes.linearProgress}/>
            }
            <div style={{height: 'calc(100% - 1px)'}}>
                <ReportTable rows={polishedReports ? polishedReports : []}/>
            </div>
        </Root>
    );
}
